'use client'
import { getNearBies } from '@/actions/places';
import ActivityPin from '@/app/itineraries/(map-layout)/components/PinMarker';
import HotelMarker from '@/components/googleMap/markers/HotelMarker';
import ImageLoading from '@/components/ImageLoading';
import CustomMarker from '@/components/leaflet/CustomMarker';
import LeafletMapContainer from '@/components/leaflet/LeafletMapContainer';
import { ActivityIconsType, placeIcons, rawIcons } from '@/components/PlaceIcon';
import Button from '@/components/ui/Button';
import { useAuthenticationModalStore } from '@/core/stores/auth';
import { useUserStore } from '@/core/stores/user';
import { PlacesBaseOnCity } from '@/types/itinerary';
import { WebbedHotel } from '@/types/webbeds';
import generateItinerary from "@/utils/loading/loading.json";
import { faCamera, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Lottie from "lottie-react";
import Image from 'next/image';
import { useEffect, useState } from 'react';

export default function HotelMapSection({ hotel }: { hotel: WebbedHotel }) {
    const [activeType, setActiveType] = useState('tourist_attraction')
    const [nearBies, setNearBys] = useState<{ [key: string]: PlacesBaseOnCity[] }>({})
    const [gettingNearBies, setIsGettingNearBies] = useState<null | string>(null)

    const { _id } = useUserStore()

    const { setShowAuthModal } = useAuthenticationModalStore()

    const handleGetNearbies = async (type: string) => {
        setActiveType(type)
        if (nearBies && nearBies[type]) {
            return
        }
        setIsGettingNearBies(type)
        try {
            const params = new URLSearchParams({
                action: 'getNearBies',
                lat: String(hotel.lat),
                lng: String(hotel.lng),
                types: type,
                limit: '15',
                radios: '5000',
                keyword: type,
            });

            const res = await fetch(`/api/places/nearby?${params.toString()}`);
            if (!res.ok) {
                throw new Error('Failed to fetch nearby places');
            }
            // console.log('👋👋👋', res);

            const { result, response } = await res.json();

            if (response.ok) {
                result && setNearBys(prev => ({ ...prev, [type]: result, }))
            }
        } catch (err) {
            console.error('Failed to fetch nearby places:', err);
            setIsGettingNearBies(null);
        }
        setIsGettingNearBies(null)

    }

    useEffect(() => {
        if (_id) {
            handleGetNearbies(activeType)
        }
    }, [_id])

    return (
        <div className='grid  md:grid-cols-[1fr,_320px] w-full h-full gap-2'>
            <div className='h-full flex flex-col'>
                <div className='w-full grow  md:h-[280px] h-52   rounded-lg overflow-hidden'>
                    <LeafletMapContainer
                        center={[+hotel.lat, +hotel.lng]}
                    >
                        {(nearBies && nearBies[activeType]?.length > 0) && nearBies[activeType].map(item =>
                            <CustomMarker key={item._id} position={[Number(item?.geometry?.location?.lat), Number(item?.geometry?.location?.lng)]}>
                                <ActivityPin
                                    icon={rawIcons[item?.types[0] as ActivityIconsType] ? rawIcons[item?.types[0] as ActivityIconsType].icon : <FontAwesomeIcon icon={faCamera} size='xl' className="w-5 h-5" />}
                                    color={rawIcons[item?.types[0] as ActivityIconsType] ? rawIcons[item?.types[0] as ActivityIconsType].color : '#7e3af2'}
                                    place={item}
                                    details={item}
                                />
                            </CustomMarker>
                        )}
                        <CustomMarker position={[hotel.lat, hotel.lng]}>
                            <HotelMarker
                                isActive
                                icon={placeIcons.Hotel}
                                color={'#f75751'}
                                place={hotel}
                            />
                        </CustomMarker>
                    </LeafletMapContainer>
                </div>
                <div className='flex md:hidden items-center gap-1 py-3'>
                    <FontAwesomeIcon icon={faLocationDot} className='text-neutral-500 text-xs' />
                    <span className='text-xs text-neutral-600 font-normal'>   {hotel?.address}</span>
                </div>
            </div>
            {_id ? <div className='flex flex-col'>
                <div className='flex items-center gap-2 mb-2'>
                    <Button size='small' radius='rounded-full' onClick={() => {
                        handleGetNearbies('tourist_attraction')
                    }} color={activeType === 'tourist_attraction' ? 'secondary' : 'ghost'}>
                        Attractions
                    </Button>
                    <Button size='small' radius='rounded-full' onClick={() => {
                        handleGetNearbies('airport,bus_station,car_rental,train_station,subway_station,taxi_stand')
                    }} color={activeType === 'airport,bus_station,car_rental,train_station,subway_station,taxi_stand' ? 'secondary' : 'ghost'}>
                        Transport
                    </Button>

                    <Button size='small' radius='rounded-full' onClick={() => {
                        handleGetNearbies('restaurant')
                    }} color={activeType === 'restaurant' ? 'secondary' : 'ghost'}>
                        Restaurant
                    </Button>
                </div>

                <div className='grow flex flex-col gap-1 md:max-h-[280px] overflow-auto min-h-52'>
                    {gettingNearBies ?
                        <div className='flex h-full items-center justify-center'>
                            <Lottie
                                className="max-w-64"
                                animationData={generateItinerary}
                                loop={true}
                            />
                        </div>
                        : (nearBies && nearBies[activeType]?.length > 0) ? nearBies[activeType]?.map(item => item?.name && <div
                            className='md:text-sm md:font-normal py-0.5' key={item.place_id}>
                            {item?.name}
                        </div>
                        ) : <div className='flex size-full items-center justify-center'>
                            <ImageLoading className='size-32 md:size-40 mx-auto' src={`assets/c0d4cab9-27fc-4f78-a956-aceba226eb64-planingo.webp`} width={300} height={300} alt="Planingo" />
                        </div>
                    }
                </div>
            </div> :
                <div className='flex flex-col items-center pb-4'>
                    <Image className='w-32 md:w-1/2' alt="Planingo" width={400} height={400}
                        src={`${process.env.NEXT_PUBLIC_STORAGE_URL}assets/c0d4cab9-27fc-4f78-a956-aceba226eb64-planingo.webp`} />
                    <p className='text-neutral-800 text-sm font-semibold mt-6'>Unlock Nearby Adventures! 🌟</p>
                    <p className='text-xs text-neutral-800 font-normal text-center mt-2'>
                        Log in to discover top attractions and dining spots around the hotel.
                    </p>

                    <Button className='w-11/12 sm:w-fit mx-auto mt-2' size='small' radius='rounded-full' onClick={() => setShowAuthModal(true)}>
                        Log in
                    </Button>
                </div>
            }
        </div>
    );
}